import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Tab, Tabs, Spinner } from "react-bootstrap";
import { api_url } from "../../constant/constant";
import { useTranslation } from "react-i18next";
import { showToast } from "../ShowToast/ToastNotification";

const AddComplianceCampaign = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [arabicError, setArabicError] = useState("");

  const [police_types, setPoliceTypes] = useState([]);
  const [formData, setFormData] = useState({
    title_en: "",
    title_ar: "",
    description_en: "",
    description_ar: "",
    start_date: "",
    end_date: "",
    start_time: "",
    type: "",
  });

  // Fetch policy types from API
  useEffect(() => {
    const fetchPoliceTypes = async () => {
      try {
        const token = localStorage.getItem("userToken");
  
        const response = await fetch(`${api_url}/compliance-campaign-types`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
  
        // Handle token expiry or auth issues
        if (response.status === 401 || response.status === 403) {
          showToast(t("SessionExpiredPleaseLoginAgain"), "error");
          localStorage.removeItem("userToken");
          navigate("/login"); // or use your preferred navigation method
          return;
        }
  
        if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
  
        const result = await response.json();
        setPoliceTypes(result.data || result);
      } catch (error) {
        showToast(t("errorFetchingData"), "error");
      }
    };
  
    fetchPoliceTypes();
  }, []);
  

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const handleArabicChange = (e) => {
    const { name, value } = e.target;
    // Allow Arabic letters, Arabic/English numbers, and spaces
    const arabicAndNumbersRegex = /^[\u0600-\u06FF0-9\s]+$/;

    if (value === "" || arabicAndNumbersRegex.test(value)) {
      setFormData({ ...formData, [name]: value });
      setArabicError(""); // Clear error
    } else {
      setArabicError(t("Please type in Arabic letters or numbers only."));
    }
  };

  // Handle form submit
  const handleAddComplianceCampaign = async (e) => {
    e.preventDefault();
    setIsLoading(true);
  
    const startDateTime = new Date(`${formData.start_date}T${formData.start_time}`);
    const now = new Date();
  
    if (startDateTime < now) {
      showToast(t("StartTimeCannotBeInPast") || "Start time cannot be in the past.", "error");
      setIsLoading(false);
      return;
    }
  
    try {
      const token = localStorage.getItem("userToken");
      const formDataToSend = new FormData();
  
      for (const key in formData) {
        formDataToSend.append(key, formData[key]);
      }
  
      const response = await fetch(`${api_url}/compliance-campaigns`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formDataToSend,
      });
  
      const result = await response.json();
  
      if (response.ok) {
        showToast(t("ComplianceCampaignAddedSuccessfully"), "success");
        setFormData({
          title_en: "",
          title_ar: "",
          description_en: "",
          description_ar: "",
          start_date: "",
          end_date: "",
          start_time: "",
          type: "",
        });
        navigate(-1);
      } else {
        if (result.status === "error" && result.errors) {
          const errorKeys = Object.keys(result.errors);
          errorKeys.forEach((key) => {
            const message = result.errors[key][0];
            showToast(message, "error");
          });
        } else {
          showToast(result.message || t("failedToAddComplianceCampaign"), "error");
        }
      }
    } catch (error) {
      showToast(t("somethingWentWrong"), "error");
    } finally {
      setIsLoading(false);
    }
  };
  

  return (
    <div
      className="addEmailContainer"
      style={{
        border: "1px solid #EAEAEA",
        borderRadius: "10px",
        padding: "26px",
        margin: "20px",
        marginBottom: "100px"
      }}
    >
      <div className="d-flex justify-content-between mb-3">
        <div className="pageTitle">{t("AddComplianceCampaign")}</div>
        <Button variant="primary" onClick={() => navigate(-1)}>
          {t("Back")}
        </Button>
      </div>

      <Tabs defaultActiveKey="Add Policy" className="mb-3">
        <Tab eventKey="Add Policy" title={t("AddComplianceCampaign")}>
          <form onSubmit={handleAddComplianceCampaign}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("Title_En")}</label>
                <input
                  type="text"
                  name="title_en"
                  className="form-control"
                  onChange={handleChange}
                  value={formData.title_en}
                  required
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("Title_Ar")}</label>
                <input
                  type="text"
                  name="title_ar"
                  className="form-control"
                  onChange={handleArabicChange}
                  value={formData.title_ar}
                  required
                />
                  {arabicError && (
                                    <div className="text-danger mt-1" style={{ fontSize: "0.9em" }}>
                                    {arabicError}
                                    </div>
                                )}
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("Description_En")}</label>
                <input
                  type="text"
                  name="description_en"
                  className="form-control"
                  onChange={handleChange}
                  value={formData.description_en}
                  required
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("Description_Ar")}</label>
                <input
                  type="text"
                  name="description_ar"
                  className="form-control"
                  onChange={handleArabicChange}
                  value={formData.description_ar}
                  required
                />
                  {arabicError && (
                                    <div className="text-danger mt-1" style={{ fontSize: "0.9em" }}>
                                    {arabicError}
                                    </div>
                                )}
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("StartDate")}</label>
                <input
                  type="date"
                  name="start_date"
                  className="form-control"
                  onChange={handleChange}
                  value={formData.start_date}
                  required
                />
              </div>
              <div className="col-md-6 mb-3">
              <label className="form-label">{t("StartTime")}</label>
              <input
                type="time"
                name="start_time"
                className="form-control"
                onChange={handleChange}
                value={formData.start_time}
                required
              />
            </div>
            </div>
            <div className="row">
            <div className="col-md-6 mb-3">
                <label className="form-label">{t("EndDate")}</label>
                <input
                  type="date"
                  name="end_date"
                  className="form-control"
                  onChange={handleChange}
                  value={formData.end_date}
                  required
                />
              </div>

            <div className="col-md-6 mb-3">
  <label className="form-label">{t("Policytype")}</label>
  <select
    name="type"
    className="form-select"
    onChange={handleChange}
    value={formData.type}
    required
  >
    <option value="">{t("SelectPolicyType")}</option>
    {police_types.map((typeItem, index) => (
      <option key={index} value={typeItem}>
        {typeItem}
      </option>
    ))}
  </select>
</div>
</div>

            <div className="text-end">
              <button type="submit" className="btn btn-primary" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {t("Adding...")}
                  </>
                ) : (
                  t("AddComplianceCampaign")
                )}
              </button>
            </div>
          </form>
        </Tab>
      </Tabs>
    </div>
  );
};

export default AddComplianceCampaign;
